import React from 'react';
import { Shield, ShieldCheck, ShieldOff, Coins, Lock, PiggyBank, Sparkles, AlertTriangle, Info, UserCheck } from 'lucide-react';
import useGameStore from '../../store/gameStore';
import { EMPTY_OBJECT } from '../../store/stableEmpties';
import { SUPPORT_ROLES } from '../../data/supportRoles';
import { useTranslation } from '../../i18n';
import { formatMoney } from '../../utils/format';
import MarkdownLite from '../MarkdownLite';

/**
 * Tax provisioning panel — Midal's signature perk.
 *
 *   • Auto-provisioning : at every daily tax accrual, the VAT owed + 25 %
 *     of the daily profit are siphoned off `treasury` into dedicated
 *     reserves (tvaProvisioned / isProvisioned). The displayed treasury
 *     therefore IS the cash-available, no more nasty surprise at year-end.
 *   • Réserve fiscale 25 % : optional layer on top — 25 % of monthly net
 *     profit is parked on a Livret-A-rated sub-account (3 % APY) released
 *     at the annual close. Doubles down on Midal's pedagogy.
 *
 *   The two toggles are stacked vertically so the player understands
 *   they are independent and can be combined. Midal hires gate the
 *   auto-provisioning ; the Réserve fiscale is open to anyone.
 */
export default function TaxProvisioningPanel() {
  const { t } = useTranslation();
  const hiredSupport = useGameStore(s => s.hiredSupport || EMPTY_OBJECT);
  const taxAutoProvisioningEnabled = useGameStore(s => s.taxAutoProvisioningEnabled);
  const fiscalReserveEnabled = useGameStore(s => s.fiscalReserveEnabled);
  const tvaProv = useGameStore(s => s.tvaProvisionedBalance || 0);
  const isProv = useGameStore(s => s.isProvisionedBalance || 0);
  const fiscalReserve = useGameStore(s => s.fiscalReserveBalance || 0);
  const interestAccrued = useGameStore(s => s.fiscalReserveInterestAccrued || 0);
  const rate = useGameStore(s => s.fiscalReserveRate || 0.03);
  const toggleProv = useGameStore(s => s.toggleTaxAutoProvisioning);
  const toggleReserve = useGameStore(s => s.toggleFiscalReserve);
  // 14/06/2026 — Délégation du paiement TVA/IS (Comptable / DAF)
  const taxAutoPayEnabled = useGameStore(s => s.taxAutoPayEnabled);
  const toggleAutoPay = useGameStore(s => s.toggleTaxAutoPay);

  const hasMarchetti = !!hiredSupport['role-pierre-marchetti'];
  const delegateId = ['role-pierre-marchetti', 'role-sophie-lambert'].find(id => hiredSupport[id]);
  const delegateDef = delegateId ? SUPPORT_ROLES.find(r => r.id === delegateId) : null;

  return (
    <div className="space-y-3" data-testid="tax-provisioning-panel">
      {/* ─── 14/06/2026 — Délégation du paiement TVA & IS (Comptable / DAF) ─── */}
      <div
        className={`rounded-lg border-2 p-3 transition-colors ${
          delegateDef && taxAutoPayEnabled
            ? 'bg-teal-950/30 border-teal-500/50'
            : delegateDef
              ? 'bg-ink-800 border-ink-600 hover:border-teal-500/40'
              : 'bg-ink-800/60 border-ink-700 opacity-80'
        }`}
        data-testid="autopay-card"
      >
        <div className="flex items-start gap-3">
          <div className={`w-10 h-10 rounded-full flex items-center justify-center flex-shrink-0 ${
            delegateDef && taxAutoPayEnabled ? 'bg-teal-500/20 text-teal-300' : 'bg-ink-700 text-slate-500'
          }`}>
            <UserCheck className="w-5 h-5" />
          </div>
          <div className="flex-1 min-w-0">
            <div className="flex items-center justify-between gap-2 flex-wrap">
              <h3 className="text-sm font-bold text-foreground flex items-center gap-1.5">
                <Sparkles className="w-3.5 h-3.5 text-teal-300" />
                {t('tax_autopay_title')}
              </h3>
              {delegateDef ? (
                <button
                  type="button"
                  onClick={() => toggleAutoPay()}
                  className={`text-[10px] font-mono font-bold uppercase tracking-wider px-2.5 py-1 rounded-full transition-colors ${
                    taxAutoPayEnabled
                      ? 'bg-teal-500/20 text-teal-300 border border-teal-500/40'
                      : 'bg-ink-700 text-slate-300 border border-ink-600 hover:border-teal-500/40'
                  }`}
                  data-testid="toggle-tax-autopay"
                >
                  {taxAutoPayEnabled ? `● ${t('tax_prov_status_on')}` : `○ ${t('tax_prov_status_off')}`}
                </button>
              ) : (
                <span className="inline-flex items-center gap-1 text-[10px] px-2 py-0.5 rounded-full bg-ink-700 text-slate-400 border border-ink-600 font-mono uppercase">
                  <Lock className="w-3 h-3" /> {t('tax_autopay_locked_badge')}
                </span>
              )}
            </div>
            <p className="text-[11px] text-slate-400 mt-1 leading-snug">
              {delegateDef
                ? <MarkdownLite as="span" text={t('tax_autopay_intro_hired', { name: delegateDef.name, role: delegateDef.role })} />
                : <MarkdownLite as="span" text={t('tax_autopay_intro_locked')} />}
            </p>
            {delegateDef && taxAutoPayEnabled && (
              <div className="mt-2 flex items-start gap-1.5 text-[10px] text-teal-200/80 bg-teal-500/10 rounded p-2 border border-teal-500/20" data-testid="autopay-delegate">
                <Info className="w-3 h-3 flex-shrink-0 mt-0.5" />
                <span>{t('tax_autopay_active_hint', { name: delegateDef.name })}</span>
              </div>
            )}
          </div>
        </div>
      </div>

      {/* ─── Midal perk : auto-provisioning ─── */}
      <div
        className={`rounded-lg border-2 p-3 transition-colors ${
          taxAutoProvisioningEnabled
            ? 'bg-emerald-950/30 border-emerald-500/50'
            : hasMarchetti
              ? 'bg-ink-800 border-ink-600 hover:border-emerald-500/40'
              : 'bg-ink-800/60 border-ink-700 opacity-80'
        }`}
        data-testid="prov-card"
      >
        <div className="flex items-start gap-3">
          <div className={`w-10 h-10 rounded-full flex items-center justify-center flex-shrink-0 ${
            taxAutoProvisioningEnabled ? 'bg-emerald-500/20 text-emerald-300' : 'bg-ink-700 text-slate-500'
          }`}>
            {taxAutoProvisioningEnabled ? <ShieldCheck className="w-5 h-5" /> : <Shield className="w-5 h-5" />}
          </div>
          <div className="flex-1 min-w-0">
            <div className="flex items-center justify-between gap-2 flex-wrap">
              <h3 className="text-sm font-bold text-foreground flex items-center gap-1.5">
                <Sparkles className="w-3.5 h-3.5 text-amber-300" />
                {t('tax_prov_title')}
              </h3>
              {hasMarchetti ? (
                <button
                  type="button"
                  onClick={() => toggleProv()}
                  className={`text-[10px] font-mono font-bold uppercase tracking-wider px-2.5 py-1 rounded-full transition-colors ${
                    taxAutoProvisioningEnabled
                      ? 'bg-emerald-500/20 text-emerald-300 border border-emerald-500/40'
                      : 'bg-ink-700 text-slate-300 border border-ink-600 hover:border-emerald-500/40'
                  }`}
                  data-testid="toggle-tax-provisioning"
                >
                  {taxAutoProvisioningEnabled ? `● ${t('tax_prov_status_on')}` : `○ ${t('tax_prov_status_off')}`}
                </button>
              ) : (
                <span className="inline-flex items-center gap-1 text-[10px] px-2 py-0.5 rounded-full bg-ink-700 text-slate-400 border border-ink-600 font-mono uppercase">
                  <Lock className="w-3 h-3" /> {t('tax_prov_locked_badge')}
                </span>
              )}
            </div>
            <p className="text-[11px] text-slate-400 mt-1 leading-snug">
              {hasMarchetti
                ? <MarkdownLite as="span" text={t('tax_prov_intro_hired')} />
                : <MarkdownLite as="span" text={t('tax_prov_intro_locked')} />}
            </p>

            {taxAutoProvisioningEnabled && (
              <div className="grid grid-cols-2 gap-2 mt-3" data-testid="prov-balances">
                <div className="rounded-md bg-ink-700/40 border border-ink-600 px-2.5 py-1.5">
                  <p className="text-[9px] uppercase tracking-wider text-slate-500 font-mono">{t('tax_prov_tva_reserve')}</p>
                  <p className="text-base font-mono font-bold text-emerald-300" data-testid="prov-tva-balance">{formatMoney(Math.round(tvaProv))}</p>
                  <p className="text-[9px] text-slate-500">{t('tax_prov_locked_until_decl')}</p>
                </div>
                <div className="rounded-md bg-ink-700/40 border border-ink-600 px-2.5 py-1.5">
                  <p className="text-[9px] uppercase tracking-wider text-slate-500 font-mono">{t('tax_prov_is_reserve')}</p>
                  <p className="text-base font-mono font-bold text-amber-300" data-testid="prov-is-balance">{formatMoney(Math.round(isProv))}</p>
                  <p className="text-[9px] text-slate-500">{t('tax_prov_locked_until_close')}</p>
                </div>
              </div>
            )}

            {taxAutoProvisioningEnabled && (
              <div className="mt-2 flex items-start gap-1.5 text-[10px] text-emerald-200/80 bg-emerald-500/10 rounded p-2 border border-emerald-500/20">
                <Info className="w-3 h-3 flex-shrink-0 mt-0.5" />
                <span><MarkdownLite text={t('tax_prov_bonus_explainer')} /></span>
              </div>
            )}
          </div>
        </div>
      </div>

      {/* ─── Optional 25 % fiscal reserve (Livret A) ─── */}
      <div
        className={`rounded-lg border-2 p-3 transition-colors ${
          fiscalReserveEnabled
            ? 'bg-sky-950/30 border-sky-500/50'
            : 'bg-ink-800 border-ink-600 hover:border-sky-500/40'
        }`}
        data-testid="reserve-card"
      >
        <div className="flex items-start gap-3">
          <div className={`w-10 h-10 rounded-full flex items-center justify-center flex-shrink-0 ${
            fiscalReserveEnabled ? 'bg-sky-500/20 text-sky-300' : 'bg-ink-700 text-slate-500'
          }`}>
            <PiggyBank className="w-5 h-5" />
          </div>
          <div className="flex-1 min-w-0">
            <div className="flex items-center justify-between gap-2 flex-wrap">
              <h3 className="text-sm font-bold text-foreground flex items-center gap-1.5">
                <Coins className="w-3.5 h-3.5 text-sky-300" />
                {t('fiscal_reserve_title', { rate: (rate * 100).toFixed(1) })}
              </h3>
              <button
                type="button"
                onClick={() => toggleReserve()}
                className={`text-[10px] font-mono font-bold uppercase tracking-wider px-2.5 py-1 rounded-full transition-colors ${
                  fiscalReserveEnabled
                    ? 'bg-sky-500/20 text-sky-300 border border-sky-500/40'
                    : 'bg-ink-700 text-slate-300 border border-ink-600 hover:border-sky-500/40'
                }`}
                data-testid="toggle-fiscal-reserve"
              >
                {fiscalReserveEnabled ? `● ${t('tax_prov_status_on')}` : `○ ${t('tax_prov_status_off')}`}
              </button>
            </div>
            <p className="text-[11px] text-slate-400 mt-1 leading-snug">
              <MarkdownLite as="span" text={t('fiscal_reserve_intro', { rate: (rate * 100).toFixed(1) })} />
            </p>

            {fiscalReserveEnabled && (
              <div className="grid grid-cols-2 gap-2 mt-3" data-testid="reserve-balances">
                <div className="rounded-md bg-ink-700/40 border border-ink-600 px-2.5 py-1.5">
                  <p className="text-[9px] uppercase tracking-wider text-slate-500 font-mono">{t('fiscal_reserve_balance_label')}</p>
                  <p className="text-base font-mono font-bold text-sky-300" data-testid="reserve-balance">{formatMoney(Math.round(fiscalReserve))}</p>
                  <p className="text-[9px] text-slate-500">{t('fiscal_reserve_locked_hint')}</p>
                </div>
                <div className="rounded-md bg-ink-700/40 border border-ink-600 px-2.5 py-1.5">
                  <p className="text-[9px] uppercase tracking-wider text-slate-500 font-mono">{t('fiscal_reserve_interest_label')}</p>
                  <p className="text-base font-mono font-bold text-money" data-testid="reserve-interest">+{formatMoney(Math.round(interestAccrued))}</p>
                  <p className="text-[9px] text-slate-500">{t('fiscal_reserve_apy', { rate: (rate * 100).toFixed(1) })}</p>
                </div>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
